/**
 * Step 4: Processing
 *
 * Uploads file and shows real-time progress from Celery task.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../../lib/api';
import { Progress } from '../../ui/progress';
import { Alert, AlertDescription } from '../../ui/alert';
import { Loader2, CheckCircle2, XCircle, Upload } from 'lucide-react';

const POLL_INTERVAL = 1000; // Poll every 1 second

export default function ProcessingStep({ file, fieldMapping, onComplete, onTaskId }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState('uploading'); // uploading, processing, success, error
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState('');
  const [taskId, setTaskId] = useState(null);
  const [error, setError] = useState(null);
  const pollIntervalRef = useRef(null);
  const uploadStartedRef = useRef(false);

  useEffect(() => {
    // Prevent double-firing in React Strict Mode (development)
    if (!uploadStartedRef.current) {
      uploadStartedRef.current = true;
      startUpload();
    }

    return () => {
      // Cleanup polling on unmount
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  const startUpload = async () => {
    try {
      setStatus('uploading');
      setProgressText(t('bulkUpload.steps.processing.status.uploading'));

      // Create FormData
      const formData = new FormData();
      formData.append('file', file);
      if (fieldMapping) {
        formData.append('field_mapping', JSON.stringify(fieldMapping));
      }

      // Upload file using apiClient
      const response = await apiClient.post('/api/students/bulk-upload/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const uploadTaskId = response.data.task_id;

      setTaskId(uploadTaskId);
      onTaskId(uploadTaskId);
      setStatus('processing');
      setProgressText(t('bulkUpload.steps.processing.status.starting'));

      // Start polling for status
      startPolling(uploadTaskId);

    } catch (err) {
      console.error('Upload error:', err);
      setStatus('error');
      setError(err.response?.data?.error || err.message || t('bulkUpload.errors.uploadFailed'));
    }
  };

  const startPolling = (uploadTaskId) => {
    pollIntervalRef.current = setInterval(() => {
      pollTaskStatus(uploadTaskId);
    }, POLL_INTERVAL);
  };

  const pollTaskStatus = async (uploadTaskId) => {
    try {
      // Use apiClient for consistent API calls
      const response = await apiClient.get(`/api/students/bulk-upload/status/${uploadTaskId}/`);
      const data = response.data;

      if (data.status === 'PENDING') {
        setProgressText(t('bulkUpload.steps.processing.status.queued'));
        setProgress(0);
      } else if (data.status === 'STARTED') {
        const progressInfo = data.progress || {};
        setProgressText(progressInfo.status || t('bulkUpload.steps.processing.status.processing'));

        if (progressInfo.total > 0) {
          const percent = (progressInfo.current / progressInfo.total) * 100;
          setProgress(percent);
        }
      } else if (data.status === 'SUCCESS') {
        // Task completed successfully
        clearInterval(pollIntervalRef.current);
        setStatus('success');
        setProgress(100);
        setProgressText(t('bulkUpload.steps.processing.status.completed'));

        // Call onComplete after a short delay for UX
        setTimeout(() => {
          onComplete(data.result);
        }, 1000);
      } else if (data.status === 'FAILURE') {
        // Task failed
        clearInterval(pollIntervalRef.current);
        setStatus('error');
        setError(data.error || t('bulkUpload.errors.processingFailed'));
      }
    } catch (err) {
      console.error('Polling error:', err);
      // Don't stop polling on network errors, might be temporary
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'uploading':
        return <Upload className="w-12 h-12 text-blue-600 animate-pulse" />;
      case 'processing':
        return <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />;
      case 'success':
        return <CheckCircle2 className="w-12 h-12 text-green-600" />;
      case 'error':
        return <XCircle className="w-12 h-12 text-red-600" />;
      default:
        return <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'uploading':
      case 'processing':
        return 'bg-blue-600';
      case 'success':
        return 'bg-green-600';
      case 'error':
        return 'bg-red-600';
      default:
        return 'bg-blue-600';
    }
  };

  return (
    <div className="space-y-6 py-8">
      {/* Status Icon */}
      <div className="flex justify-center">
        {getStatusIcon()}
      </div>

      {/* Status Text */}
      <div className="text-center">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          {status === 'uploading' && t('bulkUpload.steps.processing.title.uploading')}
          {status === 'processing' && t('bulkUpload.steps.processing.title.processing')}
          {status === 'success' && t('bulkUpload.steps.processing.title.success')}
          {status === 'error' && t('bulkUpload.steps.processing.title.error')}
        </h3>

        <p className="text-gray-600">
          {progressText}
        </p>
      </div>

      {/* Progress Bar */}
      {(status === 'uploading' || status === 'processing') && (
        <div className="space-y-2">
          <Progress value={progress} className={`h-3 ${getStatusColor()}`} />
          <div className="flex justify-between text-sm text-gray-600">
            <span>{Math.round(progress)}%</span>
            {taskId && (
              <span className="text-xs text-gray-400">
                {t('bulkUpload.steps.processing.taskId')}: {taskId.substring(0, 8)}...
              </span>
            )}
          </div>
        </div>
      )}

      {/* Error Display */}
      {status === 'error' && error && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>
            <div className="font-semibold mb-1">
              {t('bulkUpload.steps.processing.error.title')}
            </div>
            <p>{error}</p>
          </AlertDescription>
        </Alert>
      )}

      {/* Processing Info */}
      {status === 'processing' && (
        <div className="bg-blue-50 p-4 rounded-lg">
          <h4 className="font-semibold text-blue-900 mb-2">
            {t('bulkUpload.steps.processing.info.title')}
          </h4>
          <ul className="list-disc list-inside text-sm text-blue-800 space-y-1">
            <li>{t('bulkUpload.steps.processing.info.doNotClose')}</li>
            <li>{t('bulkUpload.steps.processing.info.validation')}</li>
            <li>{t('bulkUpload.steps.processing.info.flagging')}</li>
            <li>{t('bulkUpload.steps.processing.info.payments')}</li>
          </ul>
        </div>
      )}

      {/* Success Info */}
      {status === 'success' && (
        <Alert className="border-green-300 bg-green-50">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            {t('bulkUpload.steps.processing.success.message')}
          </AlertDescription>
        </Alert>
      )}

      {/* Animated Spinner */}
      {(status === 'uploading' || status === 'processing') && (
        <div className="flex justify-center">
          <div className="flex space-x-2">
            <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
            <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
            <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
          </div>
        </div>
      )}
    </div>
  );
}
